import type {MatrixEncryptedFile} from './encryptedMedia';

/**
 * Photos and videos of a room for the group panel's Multimedia tab
 * (D-CHAT-05/S04–S07): newest first, grouped by month.
 */

export type RoomMediaKind = 'photo' | 'video';

export interface RoomMediaItem {
    readonly eventId: string;
    readonly kind: RoomMediaKind;
    readonly url: string;
    readonly encryptedFile?: MatrixEncryptedFile;
    readonly mimeType?: string;
    readonly thumbnailUrl?: string;
    readonly thumbnailEncryptedFile?: MatrixEncryptedFile;
    readonly ts: number;
    readonly sender: string;
    /** "0:24" for a video whose length is known. */
    readonly duration?: string;
}

export interface RoomMediaEvent {
    getId(): string | undefined;
    getType(): string;
    getContent(): Record<string, unknown>;
    getTs(): number;
    getSender(): string | undefined;
    isRedacted(): boolean;
    isDecryptionFailure?(): boolean;
}

export type RoomMediaFilter = 'all' | 'photos' | 'videos';

const formatDuration = (ms: unknown): string | undefined => {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return undefined;
    const total = Math.round(ms / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function collectRoomMedia(events: readonly RoomMediaEvent[]): RoomMediaItem[] {
    const items: RoomMediaItem[] = [];
    for (const event of events) {
        if (event.getType() !== 'm.room.message' || event.isDecryptionFailure?.()) continue;
        const content = event.getContent();
        const msgtype = content.msgtype;
        // A redacted event has lost its content, so it can't be told apart
        // from any other deleted message; it simply leaves the grid.
        if (event.isRedacted() || (msgtype !== 'm.image' && msgtype !== 'm.video')) continue;
        const file = content.file as MatrixEncryptedFile | undefined;
        const info = (content.info ?? {}) as Record<string, unknown>;
        const url = (content.url as string | undefined) || file?.url || '';
        if (!url) continue;
        items.push({
            eventId: event.getId() ?? '',
            kind: msgtype === 'm.video' ? 'video' : 'photo',
            url,
            encryptedFile: file,
            mimeType: info.mimetype as string | undefined,
            thumbnailUrl: (info.thumbnail_url as string | undefined) || (info.thumbnail_file as MatrixEncryptedFile | undefined)?.url,
            thumbnailEncryptedFile: info.thumbnail_file as MatrixEncryptedFile | undefined,
            ts: event.getTs(),
            sender: event.getSender() ?? '',
            duration: msgtype === 'm.video' ? formatDuration(info.duration) : undefined,
        });
    }
    return items.sort((a, b) => b.ts - a.ts);
}

export function filterRoomMedia(items: readonly RoomMediaItem[], filter: RoomMediaFilter): RoomMediaItem[] {
    if (filter === 'photos') return items.filter(i => i.kind === 'photo');
    if (filter === 'videos') return items.filter(i => i.kind === 'video');
    return [...items];
}

export function countRoomMedia(items: readonly RoomMediaItem[]): Record<RoomMediaFilter, number> {
    return {
        all: items.length,
        photos: items.filter(i => i.kind === 'photo').length,
        videos: items.filter(i => i.kind === 'video').length,
    };
}

export interface RoomMediaMonth {
    readonly key: string;
    /** "October 2026" */
    readonly name: string;
    readonly items: RoomMediaItem[];
}

export function groupRoomMediaByMonth(items: readonly RoomMediaItem[], locale = 'en-GB'): RoomMediaMonth[] {
    const months: RoomMediaMonth[] = [];
    for (const item of items) {
        const date = new Date(item.ts);
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        let month = months.at(-1);
        if (!month || month.key !== key) {
            month = {key, name: date.toLocaleDateString(locale, {month: 'long', year: 'numeric'}), items: []};
            months.push(month);
        }
        month.items.push(item);
    }
    return months;
}

export function roomMediaEmptyText(filter: RoomMediaFilter): {title: string; text: string} {
    return {
        title: filter === 'photos' ? 'No photos yet' : filter === 'videos' ? 'No videos yet' : 'No photos or videos yet',
        text: filter === 'all'
            ? 'Photos and videos shared in this chat will appear here, all in one place.'
            : 'Try another filter to see the rest of the media from this chat.',
    };
}
